import { FormatHelpers } from "../helpers/format-helpers";
import { StringHelpers } from "../helpers/string-helpers";
import { IChartContainer } from "../models/base/chart-container.interface";
import { ChartStyleColorModel } from "../models/chart-style/chart-style-color.model";
import { TradeOrderDataModel } from "../models/draw-trade-order/trade-order-data.model";
import { IComponent } from "./component.interface";
import { ExitOrderPlotLineComponent } from "./exit-order-plot-line.component";
import { InformationPopupComponent } from "./information-popup.component";
import { MainSymbolCurrentPriceComponent } from "./main-symbol-current-price.component";

export class StopLossPlotLine implements IComponent {
    public id = 'sl-' + StringHelpers.newGuid();;

    public elementWidth: number | null = null;
    public elementHeight: number | null = null;
    public moveMode = false;
    public hide = false;

    public tradeId = '';

    private base: IChartContainer;
    public model = new TradeOrderDataModel();
    public price = 0;
    public profit = 0;
    private profitString = '';
    private left = 0;
    private originalLeft = 0;
    private g: any = null;
    private bracketLine: any = null;
    private bracketIcon: any = null;
    private fObj: any = null;
    private exitOrderPlotLine: ExitOrderPlotLineComponent | null = null;
    private divElement: HTMLElement | null = null;
    private lastPriceDivElement: HTMLElement | null = null;

    private color = new ChartStyleColorModel();

    private mouseUpHandler: EventListener;

    constructor(base: IChartContainer, exitOrderPlotLine: ExitOrderPlotLineComponent, left: number, model: TradeOrderDataModel) {
        this.base = base;
        this.model = model;
        this.price = model.price;
        this.exitOrderPlotLine = exitOrderPlotLine;
        this.originalLeft = left;
        this.tradeId = model.id;
        this.color = this.exitOrderPlotLine.getColor(false);

        this.calculateElementSize();
        this.mouseUpHandler = this.mouseUp.bind(this);

        this.base.tiChartSeries?.subscribeExtremeChange(this.id, () => {
            this.createIcon();
            this.createLastPrice();
        });
        this.base.tiChartSeries?.subscribeRedrawChange(this.id, () => {
            this.createIcon();
            this.createLastPrice();
        });

        this.createSytle();
        this.init();
        this.onEvents();
    }

    public remove() {
        const yAxis: any = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];
        if (!yAxis) {
            return;
        }

        yAxis.removePlotLine(this.id);
        this.removeStyle();

        try {
            this.lastPriceDivElement?.remove();
            this.g?.destroy();
            this.bracketLine?.destroy();
            this.bracketIcon?.destroy();
        } catch { }

        this.fObj = null;

        this.base.tiChartSeries.removeSubscriberByKey(this.id);
        this.base.domEvents.removeSubscribersByKey(this.id);
        this.base.tiChartSeries.element.removeEventListener('mouseup', this.mouseUpHandler);
    }

    private calculateElementSize() {
        this.setElementWidthAndHeight();
        this.left = (this.originalLeft + this.exitOrderPlotLine.elementWidth) - this.elementWidth;
        // this.left = this.originalLeft + this.exitOrderPlotLine.EXTRA_WIDTH_FOR_PROFIT_TARGET;
        // const maxWidthExceed = this.base.chartWrapper?.clientWidth - 100;
        // const currentWidth = this.left + this.elementWidth;
        // if (currentWidth > maxWidthExceed) {
        //     this.left -= currentWidth - maxWidthExceed;
        // }
    }

    private setElementWidthAndHeight() {
        const element = this.getStopLossElement();
        element.setAttribute('style', 'opacity: 0 !important; visibility: hidden !important; position: absolute !important; width: auto !important');
        this.base.tiChartSeries?.element?.insertAdjacentElement('beforeend', element);
        const width = element.clientWidth;
        const height = element.clientHeight;
        this.elementWidth = Math.ceil(width);
        this.elementHeight = Math.ceil(height);
        element.remove();
    }

    private createLastPrice() {

        const yAxis: any = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];
        if (!yAxis) return;

        const currentYValue = yAxis.toPixels(this.price) ?? 0;
        const myHeight = 7;

        const valueTop = (yAxis.toPixels(yAxis.max) ?? 0) - 16;
        const valueBottom = (yAxis.toPixels(yAxis.min) ?? 0) - 16;

        let top = currentYValue - myHeight;
        let hide = false;

        if (this.price > yAxis.max || valueTop > top) {
            top = valueTop;
            hide = true;
        } else if (this.price < yAxis.min || top > valueBottom) {
            top = valueBottom;
            hide = true;
        }

        const priceToString = '$' + FormatHelpers.numberToStringThousandSeparators(this.price, true);
        
        if (!this.lastPriceDivElement) {
            this.lastPriceDivElement = this.base.chartWrapper.querySelector<HTMLElement>('.last-price-wrapper.stop-loss');
        }

        if (this.lastPriceDivElement) {
            this.lastPriceDivElement.style.top = top + 'px';

            const pTag = this.lastPriceDivElement.querySelector('p');
            if (pTag) {
                pTag.innerHTML = priceToString;
            }
        } else {
            this.lastPriceDivElement = document.createElement('div');
            this.lastPriceDivElement.className = 'last-price-wrapper stop-loss';
            this.lastPriceDivElement.style.top = top + 'px';
            this.lastPriceDivElement.innerHTML = `
                <div class="last-price">
                    <p>${priceToString}</p>
                    <div class="line"></div>
                </div>
            `;

            const highchartContainer = this.base.tiChartSeries.element.findFirst('.highcharts-container');
            if (highchartContainer) {
                highchartContainer.insertAdjacentElement('beforeend', this.lastPriceDivElement);
            }
        }

        if (hide) {
            this.lastPriceDivElement?.classList.add('hidden');
        } else {
            this.lastPriceDivElement?.classList.remove('hidden');
        }
    }

    private getStopLossElement(dragging?: boolean): HTMLElement {
        const avgCost = (this.exitOrderPlotLine?.model?.averageCost ?? 1);
        const sharesCount = this.exitOrderPlotLine?.model?.shares ?? 0;

        // this.profit = this.price - avgCost;
        // // let percentage = (this.price / avgCost) * 100;
        // if (!this.exitOrderPlotLine?.isLong) {
        //     // percentage = (avgCost / this.price) * 100;
        //     this.profit = avgCost - this.price;
        // }

        // Stop Loss - ShareCount * (Stop Loss Price - AvgPrice)
        this.profit = sharesCount * (this.price - avgCost);

        this.profitString = FormatHelpers.numberToStringThousandSeparators(Math.abs(this.profit), true, 2);
        const priceString = FormatHelpers.numberToStringThousandSeparators(this.price, true, 2);
        const tooltip = `Stop Loss: @$${priceString}, select "X" to Cancel`;
        
        const divElement = document.createElement('div');
        divElement.title = tooltip;
        divElement.className = `stop-loss-wrapper`;
        divElement.setAttribute('style', dragging ? `cursor: -webkit-grabbing` : ``);
        divElement.innerHTML = `
            <div class="left item">
                <p class="value">${Math.abs(sharesCount)}</p>
            </div>
            <div class="center item">
                <p class="value ${this.profit >= 0 || this.profitString == '0' ? '' : 'neg'}">
                    ${this.profit == 0 || this.profitString == '0' ? '' : (this.profit > 0 ? '+' : '-')}$${this.profitString}
                </p>
            </div>
            <div class="right item">
                <svg class="exit-order" >
                    <use href="${this.base.basePath}assetfiles/img/closev2.svg#closev2"></use>
                </svg>
            </div>
        `;

        return divElement;
    }

    private removeStyle() {
        const highChartStyle = this.base.tiChartSeries?.element?.findFirst('.style-' + this.id);
        if (highChartStyle) {
            highChartStyle.remove();
        }
    }

    private createSytle() {
        this.removeStyle();
        const className = this.id;
        let styleTag = `<style>`;
        styleTag += `.highcharts-plot-line.${className} {stroke: ${this.hide ? 'transparent' : this.color.toHex()} !important;}`;
        
        styleTag += `
            .last-price-wrapper.stop-loss {
                background-color: ${this.color.toRGB()} !important;
            } 
            .last-price-wrapper.stop-loss .line {
                position: absolute;
                right: 0px;
                top: 50%;
                background-color: ${this.color.toRGB()} !important;
                height: 1px;
            }
            .last-price-wrapper.stop-loss .last-price p {
                z-index: 1;
                position: relative;
            }
            .last-price-wrapper.stop-loss .last-price:before {
                border-color: transparent ${this.color.toRGB()} transparent transparent !important;
            }
        `;

        styleTag += `</style>`;

        const chartStyleContainer = `<div class="style-${className}">${styleTag}</div>`;
        this.base.tiChartSeries?.element.insertAdjacentHTML('afterbegin', chartStyleContainer);
    }

    private init() {
        const yAxis: any = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];
        if (yAxis) {
            yAxis.addPlotLine({
                value: this.price,
                className: this.id,
                width: 1,
                id: this.id,
                zIndex: 8
            });

            this.createIcon();
            this.createLastPrice();
        }
    }

    private mouseUp() {
        if (!this.moveMode) {
            return;
        }

        this.moveMode = false;

        if (this.divElement) {
            this.divElement.style.cursor = '';
        }

        const update = () => {
            this.base.chartService.requestUpdateChartTrade(this.tradeId, this.price);
            (this.base.getComponentsByType(MainSymbolCurrentPriceComponent)?.[0] as MainSymbolCurrentPriceComponent)?.showAddButton();
            setTimeout(() => {
                this.base.tiChartSeries.preventDragging = false;

                if (!!this.g?.element) {
                    this.g?.attr({ zIndex: 18 });
                }
            }, 500);
        }

        if ((this.exitOrderPlotLine?.isLong && this.price > this.base.tiChartSeries.lastPrice) || (!this.exitOrderPlotLine?.isLong && this.price < this.base.tiChartSeries.lastPrice)) {
            setTimeout(() => {
                this.base.tiChartSeries.preventDragging = true;
            }, 50);
            const popup = this.base.getComponentsByType(InformationPopupComponent)[0] as InformationPopupComponent;
            popup?.show(`Stop Loss`, this.getStopLossAboveLastPriceMessage(), () => {
                update();
            }, undefined, () => {
                this.base.tiChartSeries.preventDragging = false;
                this.reAddPlotline(this.model.price);
            });
        } else {
            update();
        }
    }

    private reAddPlotline(yValue: number) {
        const yAxis: any = this.base.tiChartSeries.highStockChart.yAxis[0];
        if (!yAxis) {
            return;
        }

        this.price = yValue;
        yAxis.removePlotLine(this.id);
        yAxis.addPlotLine({
            value: yValue,
            className: this.id,
            width: 1,
            id: this.id,
            zIndex: 8
        });

        this.calculateElementSize();
        this.createIcon(true);
        this.createLastPrice();
    }

    private onEvents() {
        this.base.domEvents.onMouseMove(this.id, (event: any) => {
            if (!this.moveMode || !this.base.tiChartSeries) {
                return;
            }

            const yAxis: any = this.base.tiChartSeries.highStockChart.yAxis[0];
            if (!yAxis) {
                return;
            }

            if (this.divElement) {
                this.divElement.style.cursor = '-webkit-grabbing';
            }

            const pointer = this.base.tiChartSeries.highStockChart.pointer.normalize(event);

            const parentElementRect = this.base.tiChartSeries.chartElement.getBoundingClientRect();
            const yTop = pointer.y - parentElementRect.y;

            this.hide = false;

            const maxPrice = this.exitOrderPlotLine?.profitTargetPlotLine ? (this.exitOrderPlotLine?.profitTargetPlotLine?.price ?? 0) : 0;
            const yValue = yAxis.toValue(yTop);
            let yValueValidTop = yAxis.toValue(yTop + 10);
            let yValueValidBottom = yAxis.toValue(yTop - 10);
            if (yValueValidTop < yAxis.min || yValueValidBottom > yAxis.max) {
                return;
            }

            yValueValidBottom = yAxis.toValue(yTop);
            yValueValidTop = yAxis.toValue(yTop);

            // shouldn't be able to to move the Stop Loss past the Profit Target
            if (this.exitOrderPlotLine?.profitTargetPlotLine){
                if ((this.exitOrderPlotLine?.isLong && maxPrice < yValueValidBottom) || (!this.exitOrderPlotLine?.isLong && maxPrice > yValueValidBottom)) {
                    // this.hide = true;
                    return;
                }
            }

            (this.base.getComponentsByType(MainSymbolCurrentPriceComponent)?.[0] as MainSymbolCurrentPriceComponent)?.hideAddButton();
            this.reAddPlotline(yValue);
        });

        this.base.tiChartSeries.element.addEventListener('mouseup', this.mouseUpHandler);
    }

    private createIcon(dragging?: boolean) {
        const yAxis: any = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];
        if (!yAxis) return;

        try {
            if (this.g) {
                this.g?.destroy();
            }
        } catch {}

        if (dragging) {
            this.base.tiChartSeries.preventDragging = true;

            if (this.divElement)
                this.divElement.style.cursor = '-webkit-grabbing';
        }

        const renderer = this.base.tiChartSeries.highStockChart.renderer;
        this.g = renderer.g(this.id).attr({
            zIndex: 18
        });

        let top = yAxis.toPixels(this.price, true) + 46;

        const maxPrice = this.exitOrderPlotLine?.profitTargetPlotLine ? (this.exitOrderPlotLine?.profitTargetPlotLine?.price ?? 0) : yAxis.max;
        if (maxPrice >= yAxis.min && maxPrice <= yAxis.max) {
            let yValueTop = yAxis.toValue(top);
            if (yValueTop > yAxis.max) {
                top = yAxis.toPixels(yAxis.max, true) + 58;
            }

            let yValueBottom = yAxis.toValue(top);
            if (yValueBottom < yAxis.min) {
                top = yAxis.toPixels(yAxis.min, true) + 33;
            }
        }
        
        this.hide = false;
        if (this.price > yAxis.max || this.price < yAxis.min) {
            this.hide = true;
        }

        this.divElement = this.getStopLossElement(dragging);

        if (this.divElement) {
            if (this.hide) {
                this.divElement.style.display = 'none';
            } else {
                this.divElement.style.display = '';
            }
        }
        
        const foreignObjectElement = renderer.createElement('foreignObject');
        foreignObjectElement.element.appendChild(this.divElement);
        foreignObjectElement.element.setAttribute('width', this.elementWidth);
        foreignObjectElement.element.setAttribute('height', 25 + '');
        foreignObjectElement.element.setAttribute('x', this.left);
        foreignObjectElement.element.setAttribute('y', top);
        foreignObjectElement.add(this.g);

        this.fObj = foreignObjectElement;
        this.g.add();
        this.addBracketLineIfNotExist();

        if (!this.divElement || this.divElement.containClass('inited')) {
            return;
        }

        let hoverOnRemove = false;
        let hoverOnRemoveTimer: any = null;
        this.divElement.addClass('inited');
        this.divElement?.addEventListener('mousedown', () => {
            this.base.tiChartSeries.preventDragging = true;

            if (hoverOnRemove) return;
            this.moveMode = true;
        });

        this.divElement.findFirst('.right')?.ifExistThen((r: HTMLElement) => {
            const remove = () => {
                const popup = this.base.getComponentsByType(InformationPopupComponent)[0] as InformationPopupComponent;
                popup?.show(`Stop Loss`, this.getCloseStopLossMessage(), () => {
                    this.exitOrderPlotLine?.closeStopLoss();
                });
            };
            if (r.classList.contains('inited')) {
                return;
            }
            r.classList.add('inited');
            r.addEventListener('mouseenter', () => {
                clearTimeout(hoverOnRemoveTimer);
                r.classList.add('hover');
                hoverOnRemove = true;
            });
            r.addEventListener('mouseleave', () => {
                r.classList.remove('hover');
                hoverOnRemoveTimer = setTimeout(() => {
                    hoverOnRemove = false;
                }, 300);
            });
            r.addEventListener('click', (e: any) => {
                e.preventDefault();
                e.stopPropagation();
                remove();
            });

            const s = r.querySelector('svg')
            if (!s || s.classList.contains('inited')) {
                return;
            }
            s.classList.add('inited');
            s.addEventListener('click', (e: any) => {
                e.preventDefault();
                e.stopPropagation();
                remove();
            });
        });
    }

    private addBracketLineIfNotExist() {
        const yAxis = this.base.tiChartSeries.highStockChart?.yAxis?.[0];
        if (!yAxis) {
            return;
        }

        const color = this.color;
        if (this.bracketLine) {
            try {
                this.bracketLine?.destroy();
                this.bracketLine = null;
            } catch { }
        }

        if (this.bracketIcon) {
            try {
                this.bracketIcon?.destroy();
                this.bracketIcon = null;
            } catch { }
        }

        const renderer = this.base.tiChartSeries.highStockChart.renderer;
        const width = this.elementWidth;
        const EXTRA_TOP = 58;
        const currentY = yAxis.toPixels(this.price, true) + EXTRA_TOP;
        let y1 = currentY;
        let y2 = yAxis.toPixels(this.exitOrderPlotLine?.model?.averageCost, true) + EXTRA_TOP;
        const left = this.left + width + 15;

        if (this.hide) {
            y1 = yAxis.toPixels(!this.exitOrderPlotLine.isLong ? yAxis.max : yAxis.min, true) + EXTRA_TOP;

            if (this.exitOrderPlotLine.hide) {
                y2 = yAxis.toPixels(!this.exitOrderPlotLine.isLong ? yAxis.min : yAxis.max, true) + EXTRA_TOP;
            }

            this.bracketLine = renderer
                .path([`M ${left} ${y1} L ${left} ${y2} Z`])
                .attr({ 'stroke': `${color.toRGB()}`, 'stroke-width': 1 })
                .add();

            return;
        }

        if (!this.exitOrderPlotLine.hide) {
            this.bracketLine = renderer
                .path([`M ${left} ${y1} L ${left} ${y2} Z`])
                .attr({ 'stroke': `${color.toRGB()}`, 'stroke-width': 1 })
                .add();
        }
        
        this.bracketIcon = renderer.circle(left, currentY + 1.5, 3)
            .attr({
                'fill': `${color.toRGB()}`,
                'stroke': `${color.toRGB()}`,
                'stroke-width': 1.5,
                'zIndex': 12
            })
            .add();

        this.exitOrderPlotLine?.addBracketIcon();
    }

    public getCloseStopLossMessage(): string {
        const priceString = FormatHelpers.numberToStringThousandSeparators(Math.abs(this.price), true, 2);
        const html = `Remove Stop Loss exit order at $${priceString}?`;

        return `<span class="trade-message">${html}</span>`;
    }

    private getStopLossAboveLastPriceMessage(): string {
        return `<span class="trade-message">By placing your Stop Loss above Last Price, the order may execute immediately. Do you want to Continue?</span>`;
    }
}